export class StoryViewer {
  constructor(root, options = {}) {
    this.root = root;
    this.mediaController = options.mediaController;
    this.progressContainer = root?.querySelector('[data-story-progress]');
    this.image = root?.querySelector('[data-story-image]');
    this.eyebrow = root?.querySelector('[data-story-eyebrow]');
    this.title = root?.querySelector('[data-story-title]');
    this.closeButton = root?.querySelector('[data-story-close]');
    this.nextButton = root?.querySelector('[data-story-next]');
    this.prevButton = root?.querySelector('[data-story-prev]');
    this.mode = 'profile';
    this.highlights = [];
    this.highlightIndex = 0;
    this.stories = [];
    this.storyIndex = 0;
    this.rafId = null;
    this.startedAt = 0;
    this.duration = 5000;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
    this.swipeThreshold = 48;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.bindEvents();
  }

  bindEvents() {
    if (!this.root) {
      return;
    }

    this.closeButton?.addEventListener('click', () => this.close());
    this.nextButton?.addEventListener('click', () => this.next());
    this.prevButton?.addEventListener('click', () => this.previous());
    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd);
  }

  openProfile(stories) {
    this.mode = 'profile';
    this.highlights = [];
    this.highlightIndex = 0;
    this.stories = stories || [];
    this.storyIndex = 0;
    this.open();
  }

  openHighlight(highlights, highlightIndex = 0) {
    this.mode = 'highlight';
    this.highlights = highlights || [];
    this.highlightIndex = highlightIndex;
    this.stories = this.highlights[this.highlightIndex]?.stories || [];
    this.storyIndex = 0;
    this.open();
  }

  open() {
    if (!this.root || !this.stories.length) {
      return;
    }

    this.mediaController?.pauseAll();
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-overlay');
    document.addEventListener('keydown', this.handleKeydown);
    this.render();
    this.closeButton?.focus();
  }

  close() {
    if (!this.root) {
      return;
    }

    this.stopProgress();
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-overlay');
    document.removeEventListener('keydown', this.handleKeydown);
    this.image?.removeAttribute('src');
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }

    if (event.key === 'ArrowRight') {
      this.next();
    }

    if (event.key === 'ArrowLeft') {
      this.previous();
    }
  }

  handleTouchStart(event) {
    const touch = event.changedTouches?.[0];

    if (!touch) {
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
  }

  handleTouchMove(event) {
    const touch = event.changedTouches?.[0];

    if (!touch) {
      return;
    }

    this.touchDeltaX = touch.clientX - this.touchStartX;
    this.touchDeltaY = touch.clientY - this.touchStartY;
  }

  handleTouchEnd() {
    const isHorizontalSwipe = Math.abs(this.touchDeltaX) > Math.abs(this.touchDeltaY);
    const passedThreshold = Math.abs(this.touchDeltaX) >= this.swipeThreshold;

    if (isHorizontalSwipe && passedThreshold) {
      if (this.touchDeltaX < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
  }

  render() {
    const story = this.stories[this.storyIndex];

    if (!story) {
      this.close();
      return;
    }

    this.renderProgress();

    this.image.src = story.image;
    this.image.alt = story.alt || story.title || '';
    this.eyebrow.textContent = story.eyebrow || '';
    this.title.textContent = story.title || '';
    this.duration = story.duration || 5000;
    this.startProgress();
  }

  renderProgress() {
    this.progressContainer.innerHTML = this.stories
      .map((_, index) => {
        const state = index < this.storyIndex ? 'is-complete' : index === this.storyIndex ? 'is-active' : '';
        return `<span class="story-progress__item ${state}"><span class="story-progress__bar"></span></span>`;
      })
      .join('');
  }

  startProgress() {
    this.stopProgress();
    this.startedAt = performance.now();

    const activeBar = this.progressContainer.querySelector('.story-progress__item.is-active .story-progress__bar');

    const tick = (now) => {
      const elapsed = now - this.startedAt;
      const progress = Math.min(elapsed / this.duration, 1);

      if (activeBar) {
        activeBar.style.transform = `scaleX(${progress})`;
      }

      if (progress >= 1) {
        this.next();
        return;
      }

      this.rafId = window.requestAnimationFrame(tick);
    };

    this.rafId = window.requestAnimationFrame(tick);
  }

  stopProgress() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  next() {
    if (this.storyIndex < this.stories.length - 1) {
      this.storyIndex += 1;
      this.render();
      return;
    }

    if (this.mode === 'highlight' && this.highlightIndex < this.highlights.length - 1) {
      this.highlightIndex += 1;
      this.stories = this.highlights[this.highlightIndex]?.stories || [];
      this.storyIndex = 0;
      this.render();
      return;
    }

    this.close();
  }

  previous() {
    if (this.storyIndex > 0) {
      this.storyIndex -= 1;
      this.render();
      return;
    }

    if (this.mode === 'highlight' && this.highlightIndex > 0) {
      this.highlightIndex -= 1;
      this.stories = this.highlights[this.highlightIndex]?.stories || [];
      this.storyIndex = Math.max(this.stories.length - 1, 0);
      this.render();
    }
  }
}
